import React from 'react';
import PageInstructions from '../../../shared/components/PageInstructions';
import { PAGE_MODE } from '../../constants/pageModeTypes';

const DEACTIVATED_SUFFIX =
  'The user can also be activated from having an deactivated license by selecting the "Activate" button. ' +
  'Activating the user will require a license to be assigned.';

const ACTIVE_SUFFIX =
  'The user can also be deactivated from having an active license by selecting the "Deactivate" button. ' +
  'Deactivating the user will allow the license to be re-assigned to another user.';

const REMOVE_SUFFIX =
  'The user can also be removed from the license by selecting the "Remove" button. ' +
  'Removing the user will allow the license to be re-assigned to another user.';

const APPRAISER_DESCRIPTION =
  'To reassign a user to a new role, first choose the appropriate ' +
  'desired and current MDDAP roles from each respective dropdown.If applicable, ' +
  "select the affiliated date from the calendar.Third, enter the user's location " +
  'by typing their city and state or province and selecting the continents for which ' +
  "they're willing to travel(including virtual).Finally enter the years of experience " +
  'for each applicable domain.Once all the required information is entered, ' +
  'click the "Submit" button to update the license assignment to the user.';

const ADMIN_AND_FDA_DESCRIPTION =
  'To edit a user\'s license, select the "Edit" button in the upper right ' +
  'corner. Only the User Role, Organization(s), Unit(s), and OHT(s) may be edited. Select ' +
  '"Submit" to save changes. ';

const ORG_DESCRIPTION =
  'To edit a user\'s license, select the "Edit" button in the upper right ' +
  'corner. Only the User Role and Unit(s) may be edited. Select ' +
  '"Submit" to save changes. ';

const BASIC_DESCRIPTION =
  'To edit a user\'s license, select the "Edit" button in the upper right ' +
  'corner. Only the User Role may be edited. Select ' +
  '"Submit" to save changes. ';

const TIER_DESCRIPTIONS = new Map([
  [PAGE_MODE.ISACAVipOrg, ADMIN_AND_FDA_DESCRIPTION],
  [PAGE_MODE.FDAVipOrg, ADMIN_AND_FDA_DESCRIPTION],
  [PAGE_MODE.MDICVipOrg, BASIC_DESCRIPTION],
  [PAGE_MODE.MddapOrg, ORG_DESCRIPTION],
  [PAGE_MODE.OrgUnit, BASIC_DESCRIPTION],
  [PAGE_MODE.ISACAAppraiser, BASIC_DESCRIPTION]
]);

/**
 * @typedef {object} EditPageHeaderProps
 * @property {number} [pageMode]
 * @property {boolean} [isAppraiserLicense]
 * @property {boolean} [isFree]
 * @property {boolean} [isDeactivated]
 * @property {boolean} [isUserAbleToDeactivateUser]
 */

/**
 * @param {EditPageHeaderProps} props
 */
const EditPageHeader = props => {
  const {
    pageMode,
    isAppraiserLicense = false,
    isFree = false,
    isDeactivated = false,
    isUserAbleToDeactivateUser = false
  } = props;
  const description = getDescription(
    pageMode,
    isAppraiserLicense,
    isFree,
    isDeactivated,
    isUserAbleToDeactivateUser
  );

  return <PageInstructions title='Edit License' description={description} />;
};

const getDescription = (
  pageMode,
  isAppraiserLicense,
  isFree,
  isDeactivated,
  isUserAbleToDeactivateUser
) => {
  if (isAppraiserLicense) {
    return APPRAISER_DESCRIPTION;
  }

  if (isFree) {
    return getDescriptionWithStatus(BASIC_DESCRIPTION, isDeactivated, isUserAbleToDeactivateUser);
  }

  const description = TIER_DESCRIPTIONS.get(pageMode);
  return getDescriptionWithStatus(description, isDeactivated, isUserAbleToDeactivateUser);
};

const getDescriptionWithStatus = (description, isDeactivated, isUserAbleToDeactivateUser) => {
  return description + getSuffix(isDeactivated, isUserAbleToDeactivateUser);
};

const getSuffix = (isDeactivated, isUserAbleToDeactivateUser) => {
  if (!isUserAbleToDeactivateUser) {
    return REMOVE_SUFFIX;
  }

  return isDeactivated ? DEACTIVATED_SUFFIX : ACTIVE_SUFFIX;
};

export default EditPageHeader;
